package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
enum class ConversationType {
    @SerialName("direct") DIRECT,
    @SerialName("group") GROUP,
    @SerialName("unknown") UNKNOWN,
}

@Serializable
data class Conversation(
    @SerialName("id") val id: Uuid,
    @SerialName("type") val type: ConversationType = ConversationType.UNKNOWN,
    @SerialName("title") val title: String? = null,
    @SerialName("avatar_url") val avatarUrl: String? = null,
    @SerialName("members") val members: List<UserPublic> = emptyList(),
    @SerialName("last_message") val lastMessage: Message? = null,
    @SerialName("unread_count") val unreadCount: Int = 0,
    @SerialName("is_muted") val isMuted: Boolean = false,
    @SerialName("muted_until") val mutedUntil: Timestamp? = null,
    /** Archived conversations stay archived until manually restored. */
    @SerialName("is_archived") val isArchived: Boolean = false,
    @SerialName("is_pinned") val isPinned: Boolean = false,
    @SerialName("created_at") val createdAt: Timestamp = "",
    @SerialName("updated_at") val updatedAt: Timestamp = "",
)

@Serializable
data class ConversationPage(
    @SerialName("items") val items: List<Conversation> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo,
)

/** Opens or returns the existing direct conversation with `user_id`. Idempotent by design. */
@Serializable
data class CreateDirectConversationRequest(
    @SerialName("user_id") val userId: Uuid,
)

@Serializable
data class MarkReadRequest(
    @SerialName("last_read_message_id") val lastReadMessageId: Uuid,
)

@Serializable
data class TypingRequest(
    @SerialName("is_typing") val isTyping: Boolean,
)

/**
 * The contract's `ConversationMute`, returned by `muteConversation`.
 *
 * [mutedUntil] is absent for a `forever` mute rather than null-and-present, which is what
 * `explicitNulls = false` gives us. Note that the STORED column is never null: null there is how
 * "not muted" is recorded, so a forever-mute is written as a sentinel far in the future. The wire
 * shape and the column shape deliberately disagree on this point.
 */
@Serializable
data class ConversationMute(
    @SerialName("is_muted") val isMuted: Boolean,
    @SerialName("duration") val duration: String,
    @SerialName("muted_until") val mutedUntil: Timestamp? = null,
)
